import { useCallback, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { BASE_API_URL } from "./consts";
import {
  setAccessProfiles,
  setAvailablePermissions,
  setEffectivePermissions,
  setPermissionsError,
  setPermissionsLoading,
  setUserPermissions,
  setLoadedForUserId,
  resetPermissions,
} from "./redux/slices/permissionsSlice";
import { isSuperAdmin } from "./permissions";

/**
 * Centralno učitavanje permisija i access profila za trenutno ulogovanog korisnika.
 * Hook je idempotentan (puca zahteve samo kada se promeni clientId/userId).
 */
export const usePermissions = () => {
  const dispatch = useDispatch();
  const permissionState = useSelector((state: any) => state.permissions);
  const userData = useSelector((state: any) => state.userData.user);
  const fallbackPermissions: string[] = userData?.permissions || [];
  const isSuperUser = isSuperAdmin(userData?.roles);

  const clientId = userData?.clientId?._id || userData?.clientId;
  const userId = userData?.id || userData?._id;

  useEffect(() => {
    if (!clientId || !userId) return;

    // Ako su permisije već učitane za ovog istog usera — preskoči fetch
    if (
      permissionState.loadedForUserId === userId &&
      permissionState.availablePermissions.length &&
      (permissionState.effectivePermissions.length ||
        permissionState.userPermissions.length) &&
      permissionState.accessProfiles.length
    ) {
      return;
    }

    // User se promijenio — resetuj stare permisije prije fetcha
    if (permissionState.loadedForUserId && permissionState.loadedForUserId !== userId) {
      dispatch(resetPermissions());
    }

    dispatch(setPermissionsLoading(true));
    dispatch(setPermissionsError(undefined));

    const fetchData = async () => {
      try {
        const [allPermissionsRes, userPermissionsRes, profilesRes] =
          await Promise.all([
            axios.get(`${BASE_API_URL}/admin/${clientId}/permissions`),
            axios.get(
              `${BASE_API_URL}/admin/${clientId}/users/${userId}/permissions`
            ),
            axios.get(`${BASE_API_URL}/admin/${clientId}/access-profiles`),
          ]);

        dispatch(
          setAvailablePermissions(
            (allPermissionsRes.data || []).map((p: any) => p.key)
          )
        );
        dispatch(
          setUserPermissions(userPermissionsRes.data?.permissions || [])
        );
        dispatch(
          setEffectivePermissions(
            userPermissionsRes.data?.effectivePermissions || []
          )
        );
        dispatch(setAccessProfiles(profilesRes.data || []));
        dispatch(setLoadedForUserId(userId));
      } catch (error: any) {
        const message =
          error?.response?.data?.message || "Failed to load permissions";
        dispatch(setPermissionsError(message));
        console.error(error);
      } finally {
        dispatch(setPermissionsLoading(false));
      }
    };

    fetchData();
  }, [clientId, userId, dispatch]);

  const hasPermission = useCallback(
    (permissionKey?: string) => {
      if (!permissionKey) return true;
       // Super admin preskače sve provere
      if (isSuperUser) return true;
      // Dok se ne učita i nemamo efektivne permisije, tretiramo kao da nema dozvole
      if (
        permissionState.loading &&
        !permissionState.effectivePermissions?.length
      ) {
        return false;
      }
      if (
        !permissionState.loading &&
        (!permissionState.effectivePermissions ||
          !permissionState.effectivePermissions.length)
      ) {
        return fallbackPermissions.includes(permissionKey);
      }
      const effectiveList =
        permissionState.effectivePermissions?.length
          ? permissionState.effectivePermissions
          : fallbackPermissions;
      return effectiveList.includes(permissionKey);
    },
    [
      fallbackPermissions,
      permissionState.effectivePermissions,
      permissionState.loading,
      permissionState.availablePermissions,
      isSuperUser,
    ]
  );

  const hasAnyPermission = useCallback(
    (permissionKeys: string[] = []) => {
      return permissionKeys.some((key) => hasPermission(key));
    },
    [hasPermission]
  );

  return {
    ...permissionState,
    hasPermission,
    hasAnyPermission,
  };
};
